/**
 * Normaliza payload de pedido (API nova ou legado) para o modal de conversão.
 * Não expõe preços — campos de valor são ignorados na UI.
 */

function normId(pid) {
  if (pid == null || pid === '') return ''
  return String(pid).trim()
}

function normLink(link) {
  if (link == null || link === '') return ''
  let s = String(link).trim()
  if (/^https?:\/\//i.test(s)) {
    try {
      const u = new URL(s)
      s = u.pathname || s
    } catch {
      s = s.replace(/^https?:\/\/[^/]+/i, '')
    }
  }
  return s
    .replace(/^\//, '')
    .split('?')[0]
    .replace(/\/$/, '')
    .toLowerCase()
}

function mapRecommendedItem(item) {
  const meta = item.metadata && typeof item.metadata === 'object' ? item.metadata : {}
  return {
    source: 'recommended',
    productId: item.product_id,
    name: item.product_name || (item.product_id != null ? `Produto ${item.product_id}` : 'Produto'),
    image: item.link_imagem || meta.link_imagem || null,
    link: item.link_produto || meta.link_produto || null,
    subtitle: item.reason || null,
    algorithm: item.algorithm || null,
    confidenceScore: item.confidence_score != null ? item.confidence_score : null,
    categoria: meta.categoria || null,
    popularity: meta.popularity != null ? meta.popularity : null,
  }
}

function enrichPurchasedLine(item, opts = {}) {
  const meta = item.metadata && typeof item.metadata === 'object' ? item.metadata : {}
  const purchased = opts.forceLineIsPurchased
    ? true
    : item.purchased !== false
  return {
    source: 'purchased',
    productId: item.product_id,
    name: item.product_name || meta.product_name || (item.product_id != null ? `Produto ${item.product_id}` : 'Produto'),
    image: item.link_imagem || meta.link_imagem || null,
    link: item.link_produto || meta.link_produto || null,
    subtitle: null,
    purchased,
    converted: !!item.conversao_recomendacao,
    quantidade: item.quantidade != null && item.quantidade !== '' ? item.quantidade : null,
  }
}

/**
 * Itens efetivamente comprados.
 * - `produtos_comprados` (legado): a própria lista é o pedido; cada linha conta como comprada
 *   (flags `purchased`/`conversao_recomendacao` são metadados, não exclusão).
 * - `produtos_pedido`: purchased === true ou conversao_recomendacao; se ninguém tiver purchased
 *   true/false explícito, considera todos (lista só de comprados).
 */
function selectPurchasedLines(items, { legacyCompradosList = false } = {}) {
  if (!Array.isArray(items) || !items.length) return []
  if (legacyCompradosList) {
    return items.map(it => enrichPurchasedLine(it, { forceLineIsPurchased: true }))
  }
  const explicit = items.some(it => it.purchased === true || it.purchased === false)
  if (!explicit) {
    return items.map(it => enrichPurchasedLine(it, {}))
  }
  return items
    .filter(it => it.purchased === true || it.conversao_recomendacao === true)
    .map(it => enrichPurchasedLine(it, {}))
}

function getPurchasedMatch(rec, purchasedBase) {
  const rid = normId(rec.productId)
  if (rid) {
    const byId = purchasedBase.find(p => normId(p.productId) === rid)
    if (byId) return byId
  }
  const rlink = normLink(rec.link)
  if (rlink) {
    for (let i = 0; i < purchasedBase.length; i += 1) {
      const p = purchasedBase[i]
      const pl = normLink(p.link)
      if (pl && (pl === rlink || pl.includes(rlink) || rlink.includes(pl))) return p
    }
  }
  return null
}

/**
 * Unifica formatos: `produtos_pedido` (novo) ou `produtos_comprados` (legado), ids de pedido e datas.
 */
export function coerceRecommendationOrderPayload(raw) {
  if (!raw || typeof raw !== 'object') {
    return {
      pedido_id: '',
      created_at: null,
      produtos_pedido: [],
      produtos_recomendados: [],
      lead: null,
      legacyCompradosList: false,
    }
  }
  let produtosPedido = []
  let legacyCompradosList = false
  if (Array.isArray(raw.produtos_pedido)) {
    produtosPedido = raw.produtos_pedido
  } else if (Array.isArray(raw.produtos_comprados)) {
    produtosPedido = raw.produtos_comprados
    legacyCompradosList = true
  }
  const recs = Array.isArray(raw.produtos_recomendados) ? raw.produtos_recomendados : []
  let pedidoId = raw.pedido_id != null ? String(raw.pedido_id) : ''
  if (!pedidoId && raw.order_id != null) pedidoId = String(raw.order_id)
  if (!pedidoId && raw.order_key != null) {
    pedidoId = String(raw.order_key).replace(/^oid:/i, '')
  }
  const createdAt = raw.created_at || raw.purchased_at || null
  const lead = raw.lead
    || (raw.lead_name ? { name: raw.lead_name, phone: raw.telefone || raw.phone } : null)
  return {
    pedido_id: pedidoId,
    created_at: createdAt,
    produtos_pedido: produtosPedido,
    produtos_recomendados: recs,
    lead,
    legacyCompradosList,
  }
}

/**
 * @param {object} rawData - objeto pedido cru (legado ou novo formato)
 * @returns {object} shape consumido pelo modal
 */
export default function normalizeRecommendationOrderData(rawData) {
  const data = coerceRecommendationOrderPayload(rawData)
  const orderId = data.pedido_id || '—'
  const purchasedAt = data.created_at

  const recommended = (data.produtos_recomendados || []).map(mapRecommendedItem)
  const purchasedBase = selectPurchasedLines(data.produtos_pedido || [], {
    legacyCompradosList: data.legacyCompradosList === true,
  })

  const recommendedCount = recommended.length
  const purchasedCount = purchasedBase.length

  const convertedProducts = []
  for (let i = 0; i < recommended.length; i += 1) {
    const rec = recommended[i]
    const match = getPurchasedMatch(rec, purchasedBase)
    if (match) {
      convertedProducts.push({
        ...rec,
        purchased: true,
        converted: true,
        quantidade: match.quantidade != null ? match.quantidade : null,
      })
    }
  }

  const recommendedNotPurchased = recommended.filter(r => !getPurchasedMatch(r, purchasedBase))

  const purchasedNotRecommended = purchasedBase.filter(p => {
    const pid = normId(p.productId)
    const plink = normLink(p.link)
    for (let k = 0; k < recommended.length; k += 1) {
      const rec = recommended[k]
      if (normId(rec.productId) && normId(rec.productId) === pid) return false
      const rl = normLink(rec.link)
      if (plink && rl && (plink === rl || plink.includes(rl) || rl.includes(plink))) return false
    }
    return true
  })

  const convertedCount = convertedProducts.length
  let conversionRatePct = null
  if (recommendedCount > 0) {
    conversionRatePct = Math.round((convertedCount / recommendedCount) * 1000) / 10
  }

  const hasRecommendations = recommendedCount > 0
  const hasPurchased = purchasedCount > 0

  return {
    orderId,
    purchasedAt,
    leadName: data.lead && (data.lead.name || data.lead.nome) ? (data.lead.name || data.lead.nome) : null,
    recommendedCount,
    purchasedCount,
    convertedCount,
    conversionRatePct,
    conversionRateLabel:
      recommendedCount > 0
        ? `${Number.isInteger(conversionRatePct) ? conversionRatePct : conversionRatePct.toFixed(1)}% das recomendações convertidas`
        : null,
    convertedProducts,
    recommendedNotPurchased,
    purchasedNotRecommended,
    hasRecommendations,
    hasPurchased,
    showNoRecommendationMessage: !hasRecommendations,
    showNoPurchasedMessage: !hasPurchased,
    showNoConversionMessage: hasRecommendations && hasPurchased && convertedCount === 0,
  }
}
